import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export interface LegalSection {
  id: string
  title: string
  body: string[]
}

export interface LegalRelatedLink {
  label: string
  path: string
}

export interface LegalPageLayoutProps {
  title: string
  updatedAt: string
  intro: ReactNode
  disclaimer?: ReactNode
  sections: LegalSection[]
  relatedLinks: LegalRelatedLink[]
}

export default function LegalPageLayout({
  title,
  updatedAt,
  intro,
  disclaimer,
  sections,
  relatedLinks,
}: LegalPageLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '')
  const observerRef = useRef<IntersectionObserver | null>(null)

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveId(id)
    }
  }

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return

    observerRef.current?.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0, 0.25, 0.5] },
    )

    sections.forEach((section) => {
      const el = document.getElementById(section.id)
      if (el) observerRef.current?.observe(el)
    })

    return () => observerRef.current?.disconnect()
  }, [sections])

  return (
    <div className="legal-layout">
      <aside className="legal-toc" aria-label={t('legal.tocTitle')}>
        <div className="legal-toc-title">{t('legal.tocTitle')}</div>
        <nav className="legal-toc-list">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`legal-toc-link ${activeId === section.id ? 'is-active' : ''}`}
              onClick={() => scrollToSection(section.id)}
            >
              {section.title}
            </button>
          ))}
        </nav>
      </aside>

      <div className="legal-body">
        <div className="legal-mobile-toc">
          <div className="legal-mobile-toc-scroll">
            {sections.map((section) => (
              <button
                key={`mobile-${section.id}`}
                type="button"
                className={`legal-mobile-toc-pill ${activeId === section.id ? 'is-active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        <header className="legal-hero">
          <h1 className="legal-hero-title">{title}</h1>
          <p className="legal-hero-meta">
            {t('public.updatedAt')}: {updatedAt}
          </p>
          <div className="legal-hero-intro">{intro}</div>
          {disclaimer && <div className="legal-disclaimer">{disclaimer}</div>}
        </header>

        {sections.map((section) => (
          <section key={section.id} id={section.id} className="legal-section">
            <h2 className="legal-section-title">{section.title}</h2>
            {section.body.map((paragraph, index) => (
              <p key={`${section.id}-${index}`}>{paragraph}</p>
            ))}
          </section>
        ))}

        <div className="legal-related">
          <span className="legal-related-label">{t('legal.relatedLinks')}</span>
          {relatedLinks.map((link) => (
            <button
              key={link.path}
              type="button"
              className="public-btn-secondary"
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </button>
          ))}
          <button type="button" className="public-btn-ghost" onClick={() => navigate('/')}>
            {t('common.backHome')}
          </button>
        </div>
      </div>
    </div>
  )
}
